import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Bell,
  Loader2,
  Power,
  Play,
  Square,
  UserPlus,
  ShieldAlert,
  Database,
  Server,
  Activity,
  type LucideIcon,
} from 'lucide-react';
import { clsx } from 'clsx';
import { fetchAuditLogs } from '@/api/audit';
import type { AuditLogEntry } from '@/types';

export default function NotificationsDropdown() {
  const navigate = useNavigate();
  const containerRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);

  const [lastSeenAt, setLastSeenAt] = useState<string>(() => {
    try { return localStorage.getItem(LAST_SEEN_KEY) ?? ''; } catch { return ''; }
  });

  const { data, isLoading, isError } = useQuery({
    queryKey: ['notifications', 'recent'],
    queryFn: () => fetchAuditLogs({ page: 1, page_size: 15 }),
    staleTime: 30_000,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
    retry: false,
  });

  const entries = data?.items ?? [];

  const unreadCount = useMemo(() => {
    if (!lastSeenAt) return entries.length;
    return entries.filter((e) => e.created_at > lastSeenAt).length;
  }, [entries, lastSeenAt]);

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  function handleToggle() {
    const willOpen = !open;
    setOpen(willOpen);
    if (willOpen && entries.length > 0) {
      const newest = entries[0].created_at;
      try { localStorage.setItem(LAST_SEEN_KEY, newest); } catch { /* noop */ }
      setLastSeenAt(newest);
    }
  }

  return (
    <div className="relative" ref={containerRef}>
      <button
        onClick={handleToggle}
        className="group relative rounded-lg p-2 text-slate-400 transition-all duration-200 hover:bg-slate-100 hover:text-slate-600 dark:text-slate-500 dark:hover:bg-slate-800 dark:hover:text-slate-300"
        aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} new)` : ''}`}
      >
        <Bell className="h-[18px] w-[18px]" />
        {unreadCount > 0 && (
          <span className="absolute right-1 top-1 flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-brand-400 opacity-75" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-brand-500 ring-2 ring-white dark:ring-slate-900" />
          </span>
        )}
      </button>

      {open && (
        <div className="animate-scale-in absolute right-0 top-full z-50 mt-2 w-96 max-w-[calc(100vw-2rem)] overflow-hidden rounded-xl bg-white shadow-dropdown dark:bg-slate-800 dark:ring-1 dark:ring-slate-700">
          <div className="flex items-center justify-between border-b border-slate-100 px-5 py-3.5 dark:border-slate-700/60">
            <div>
              <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">Activity</h3>
              <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">Recent platform events</p>
            </div>
            {unreadCount > 0 && (
              <span className="rounded-full bg-brand-50 px-2 py-0.5 text-[10px] font-semibold text-brand-700 dark:bg-brand-950/50 dark:text-brand-300">
                {unreadCount} new
              </span>
            )}
          </div>

          <div className="max-h-96 overflow-y-auto">
            {isLoading && (
              <div className="flex items-center justify-center gap-2 px-4 py-10 text-sm text-slate-500">
                <Loader2 className="h-4 w-4 animate-spin text-brand-500" />
                Loading…
              </div>
            )}

            {isError && (
              <div className="px-4 py-8 text-center text-sm text-slate-500 dark:text-slate-400">
                <ShieldAlert className="mx-auto mb-2 h-5 w-5 text-slate-400" />
                Couldn&apos;t load activity.
                <p className="mt-1 text-xs">Admin access may be required.</p>
              </div>
            )}

            {!isLoading && !isError && entries.length === 0 && (
              <div className="px-4 py-10 text-center text-sm text-slate-500 dark:text-slate-400">
                <Bell className="mx-auto mb-2 h-5 w-5 text-slate-300 dark:text-slate-600" />
                No activity yet.
              </div>
            )}

            {!isLoading && !isError && entries.length > 0 && (
              <ul className="divide-y divide-slate-100/80 dark:divide-slate-700/40">
                {entries.map((entry) => (
                  <NotificationRow key={entry.id} entry={entry} isUnread={!lastSeenAt || entry.created_at > lastSeenAt} />
                ))}
              </ul>
            )}
          </div>

          <div className="border-t border-slate-100 bg-surface-50 px-5 py-3 dark:border-slate-700/60 dark:bg-slate-900/50">
            <button
              onClick={() => { setOpen(false); navigate('/admin/audit-logs'); }}
              className="w-full text-center text-xs font-semibold text-brand-600 transition-colors hover:text-brand-700 dark:text-brand-400 dark:hover:text-brand-300"
            >
              View all audit logs →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function NotificationRow({ entry, isUnread }: { entry: AuditLogEntry; isUnread: boolean }) {
  const { icon: Icon, iconBg, iconColor } = decorateAction(entry.action, entry.resource_type);
  const headline = describeEntry(entry);
  const target = describeTarget(entry);
  const actor = describeActor(entry);

  return (
    <li className={clsx('flex gap-3 px-5 py-3 transition-colors hover:bg-slate-50 dark:hover:bg-slate-700/30', isUnread && 'bg-brand-50/30 dark:bg-brand-950/10')}>
      <div className={clsx('mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg', iconBg)}>
        <Icon className={clsx('h-3.5 w-3.5', iconColor)} />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-[13px] leading-relaxed text-slate-700 dark:text-slate-200">
          <span className="font-semibold">{actor}</span>{' '}
          <span className="text-slate-500 dark:text-slate-400">{headline}</span>{' '}
          {target && <span className="font-mono text-xs text-slate-600 dark:text-slate-300">{target}</span>}
        </p>
        <p className="mt-0.5 text-[11px] text-slate-400 dark:text-slate-500">{formatRelativeTime(entry.created_at)}</p>
      </div>
    </li>
  );
}

const LAST_SEEN_KEY = 'circuitry:notifications:last_seen_at';

function decorateAction(action: string, resourceType: string): { icon: LucideIcon; iconBg: string; iconColor: string } {
  if (action.endsWith('.stopped') || action.endsWith('.auto_stopped'))
    return { icon: Square, iconBg: 'bg-slate-100 dark:bg-slate-800', iconColor: 'text-slate-500 dark:text-slate-400' };
  if (action.endsWith('.started') || action.endsWith('.auto_started'))
    return { icon: Play, iconBg: 'bg-emerald-50 dark:bg-emerald-950/40', iconColor: 'text-emerald-600 dark:text-emerald-400' };
  if (resourceType === 'user')
    return { icon: UserPlus, iconBg: 'bg-brand-50 dark:bg-brand-950/40', iconColor: 'text-brand-600 dark:text-brand-400' };
  if (resourceType === 'ec2_instance')
    return { icon: Server, iconBg: 'bg-amber-50 dark:bg-amber-950/40', iconColor: 'text-amber-600 dark:text-amber-400' };
  if (resourceType === 'rds_instance')
    return { icon: Database, iconBg: 'bg-violet-50 dark:bg-violet-950/40', iconColor: 'text-violet-600 dark:text-violet-400' };
  if (resourceType === 'kubernetes_namespace')
    return { icon: Power, iconBg: 'bg-sky-50 dark:bg-sky-950/40', iconColor: 'text-sky-600 dark:text-sky-400' };
  return { icon: Activity, iconBg: 'bg-slate-100 dark:bg-slate-800', iconColor: 'text-slate-500 dark:text-slate-400' };
}

function describeActor(entry: AuditLogEntry): string {
  const details = entry.details ?? {};
  if (details.trigger === 'system:scheduler') return 'Scheduler';
  if (entry.actor_display_name) return entry.actor_display_name;
  if (entry.actor_email) return entry.actor_email;
  if (details.actor_display_name) return String(details.actor_display_name);
  if (details.actor_email) return String(details.actor_email);
  return 'Someone';
}

function describeEntry(entry: AuditLogEntry): string {
  switch (entry.action) {
    case 'namespace.stopped': return 'stopped namespace';
    case 'namespace.started': return 'started namespace';
    case 'namespace.auto_stopped': return 'auto-stopped namespace';
    case 'namespace.auto_started': return 'auto-started namespace';
    case 'aws.ec2.started': return 'started EC2 instance';
    case 'aws.ec2.stopped': return 'stopped EC2 instance';
    case 'aws.ec2.auto_started': return 'auto-started EC2';
    case 'aws.ec2.auto_stopped': return 'auto-stopped EC2';
    case 'aws.rds.started': return 'started RDS database';
    case 'aws.rds.stopped': return 'stopped RDS database';
    case 'aws.rds.auto_started': return 'auto-started RDS';
    case 'aws.rds.auto_stopped': return 'auto-stopped RDS';
    case 'user.created': return 'created user';
    case 'user.role_changed': return 'changed role for';
    case 'user.deactivated': return 'deactivated user';
    case 'user.reactivated': return 'reactivated user';
    case 'user.password_reset': return 'reset password for';
    default: return entry.action.replace(/[._]/g, ' ');
  }
}

function describeTarget(entry: AuditLogEntry): string | null {
  const details = entry.details ?? {};
  return (
    (details.namespace as string | undefined) ??
    (details.instance_id as string | undefined) ??
    (details.db_identifier as string | undefined) ??
    (details.email as string | undefined) ??
    entry.resource_id ?? null
  );
}

function formatRelativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return '';
  const diffSec = Math.round((Date.now() - then) / 1000);
  if (diffSec < 5) return 'just now';
  if (diffSec < 60) return `${diffSec}s ago`;
  const diffMin = Math.round(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.round(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.round(diffHr / 24);
  if (diffDay === 1) return 'Yesterday';
  if (diffDay < 7) return `${diffDay}d ago`;
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}
